import { useEffect, useState } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useAppDispatch } from "store";
import { setGlobalComponent } from "store/global-components";
import { useAppTranslation } from "locale/useAppTranslation";
import { useVehicleAuxiliaryServiceV1BatchCreateVehicleAuxiliaryMutation } from "_services/modules/vehicle-auxiliary";
import { toast } from "react-toastify";
import { errorHandler } from "_services/errorHandler";
interface AuxiliaryItem {
  auxiliaryName: string;
  status: string;
}

export interface AddVehicleAuxiliaryFormValues {
  auxiliaryName: string;
  status: string;
  auxiliaries: AuxiliaryItem[];
}

const schema = yup.object({
  auxiliaryName: yup.string().required(),
  status: yup.string().required(),
  auxiliaries: yup.array().of(
    yup.object({
      auxiliaryName: yup.string().required(),
      status: yup.string().required(),
    })
  ).required(),
}).required();

const useAddVehicleAuxiliaryForm = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useAppTranslation("vehicleAuxiliary");

  const {
    control,
    register,
    trigger,
    getValues,
    setValue,
    setError,
    formState: { errors },
  } = useForm<AddVehicleAuxiliaryFormValues>({
    mode: "onBlur",
    resolver: yupResolver(schema),
    defaultValues: {
      auxiliaryName: "",
      status: "active",
      auxiliaries: [],
    },
  });

  const { fields, append, remove, update } = useFieldArray({
    control,
    name: "auxiliaries",
  });

  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [showSaveModal, setShowSaveModal] = useState(false);

  const [batchCreateVehicleAuxiliary] = useVehicleAuxiliaryServiceV1BatchCreateVehicleAuxiliaryMutation();

  useEffect(() => {
    dispatch(
      setGlobalComponent({
        title: t("page-title-add"),
        hasBackButton: true,
      })
    );
  }, [dispatch, t]);

  const handleAddToList = async () => {
    const isValid = await trigger(["auxiliaryName", "status"]);
    if (!isValid) return;

    const { auxiliaryName, status } = getValues();

    const isDuplicate = fields.some(
      (item, index) =>
        item.auxiliaryName.toLowerCase() === auxiliaryName.toLowerCase() &&
        index !== editingIndex
    );

    if (isDuplicate) {
      setError("auxiliaryName", {
        type: "validate",
        message: t("error-duplicate-value"),
      });
      return;
    }

    if (editingIndex !== null) {
      update(editingIndex, { auxiliaryName, status });
      setEditingIndex(null);
    } else {
      append({ auxiliaryName, status });
    }

    setValue("auxiliaryName", "");
    setValue("status", "active");
  };

  const handleEditItem = (index: number) => {
    setEditingIndex(index);
    setValue("auxiliaryName", fields[index].auxiliaryName);
    setValue("status", fields[index].status);
  };

  const handleDeleteItem = (index: number) => {
    remove(index);
    if (editingIndex === index) {
      setEditingIndex(null);
      setValue("auxiliaryName", "");
      setValue("status", "active");
    } else if (editingIndex !== null && index < editingIndex) {
      setEditingIndex(editingIndex - 1);
    }
  };

  const handleDiscardChanges = () => {
    setEditingIndex(null);
    setValue("auxiliaryName", "");
    setValue("status", "active");
  };

  const handleSave = async () => {
    const payload = fields.map((f) => ({
      auxiliary_name: f.auxiliaryName,
      status: f.status,
    }));
    try {
      await batchCreateVehicleAuxiliary({
        vehicleAuxiliaryV1BatchCreateVehicleAuxiliaryReq: { items: payload },
      }).unwrap();
      toast.success(t("toast-create-success"));
      setShowSaveModal(false);
      navigate(-1);
    } catch (error) {
      errorHandler(error);
      setShowSaveModal(false);
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  return {
    control,
    register,
    errors,
    fields,
    editingIndex,
    showSaveModal,
    setShowSaveModal,
    handleAddToList,
    handleEditItem,
    handleDeleteItem,
    handleDiscardChanges,
    handleSave,
    handleCancel,
    t,
  };
};

export default useAddVehicleAuxiliaryForm;
